import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const TOKEN_KEY = "uh_atelier_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
function writeToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(readToken);
  const [admin, setAdmin] = useState(null);
  // status: "loading" | "authenticated" | "anonymous"
  const [status, setStatus] = useState(token ? "loading" : "anonymous");

  const logout = useCallback(() => {
    writeToken(null);
    setToken(null);
    setAdmin(null);
    setStatus("anonymous");
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    writeToken(data.access_token);
    setToken(data.access_token);
    setAdmin({ ...data.admin, must_change_password: !!data.must_change_password });
    setStatus("authenticated");
    return { admin: data.admin, mustChangePassword: !!data.must_change_password };
  }, []);

  // Called by the change-password page after a successful password change
  // to clear the `must_change_password` flag from local admin state.
  const clearMustChangePassword = useCallback(() => {
    setAdmin((a) => (a ? { ...a, must_change_password: false } : a));
  }, []);

  // On boot, validate token by calling /auth/me
  useEffect(() => {
    if (!token) return;
    let alive = true;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!alive) return;
        setAdmin({ ...r.data, must_change_password: !!r.data.must_change_password });
        setStatus("authenticated");
      })
      .catch(() => {
        if (!alive) return;
        writeToken(null);
        setToken(null);
        setAdmin(null);
        setStatus("anonymous");
      });
    return () => { alive = false; };
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, admin, status, login, logout, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Convenience: an axios instance that always attaches the current token.
export function useAuthedAxios() {
  const { token } = useAuth();
  return {
    get: (path, opts = {}) => axios.get(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } }),
    post: (path, body, opts = {}) => axios.post(`${API}${path}`, body, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` } }),
    downloadCsv: async (path, filename) => {
      const r = await axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  };
}
